import { CONTROL_DEFINITIONS } from "./controls.js";
/**
 * @param {HTMLElement} containerElement - Container to append into.
 * @returns {HTMLDivElement | null}
 */
const createTextToSpeechContainer = (containerElement) => {
    if (!containerElement) {
        return null;
    }

    const textToSpeechContainer = document.createElement("div");
    textToSpeechContainer.className = "wl-speech-player-container";

    containerElement.appendChild(textToSpeechContainer);
    return textToSpeechContainer;
};

/**
 * Create one button per option.
 *
 * @param {string[]} options - List of option names, e.g. ["playPause"].
 * @param {SpeechSynthesis} synth - Web Speech synthesis instance.
 * @param {SpeechSynthesisUtterance} utterance - Utterance to control.
 * @param {{isPlaying: boolean, isPaused: boolean}} state - Shared player state.
 * @param {string} text - Text to speech.
 * @param {Translations} translations
 * @returns {HTMLButtonElement[]} - Array of created buttons.
 */
const createButtons = (options, synth, utterance, state, text, translations) => {
    /** @type {HTMLButtonElement[]} */
    const buttons = [];

    for (const name of options) {
        const controlDefinition = CONTROL_DEFINITIONS[name];
        if (!controlDefinition) {
            console.warn(`${translations.unsupported_option_ignored} "${name}"`);
            continue;
        }

        const button = document.createElement("button");
        button.type = "button";
        button.className = "wl-speech-player-button";
        button.ariaLabel = name;
        button.dataset.control = name;

        controlDefinition({
            button,
            synth,
            utterance,
            state,
            text,
        });

        buttons.push(button);
    }

    return buttons;
};

/**
 * Prepares the button container and creates buttons
 *
 * @param {string[]} options - List of option names, e.g. ["playPause"].
 * @param {SpeechSynthesis} synth - Web Speech synthesis instance.
 * @param {SpeechSynthesisUtterance} utterance - Utterance to control.
 * @param {{isPlaying: boolean, isPaused: boolean}} playerState - Shared player state.
 * @param {string} text - Text to be read.
 * @param {Translations} translations
 * @returns {HTMLDivElement}
 */
const prepareButtonContainer = (options, synth, utterance, playerState, text, translations) => {
    const buttonContainer = document.createElement("div");
    buttonContainer.className = "wl-speech-player-button-container";

    const buttons = createButtons(options, synth, utterance, playerState, text, translations);

    buttons.forEach((button) => {
        buttonContainer.appendChild(button);
    });

    return buttonContainer;
};

/**
 * Create a speech player instance.
 *
 * @param {HTMLElement} rootElement - Element to read text from.
 * @param {HTMLElement} containerElement - Element to append the player into.
 * @param {string[]} [options=["playPause"]] - Control names to enable.
 * @param {Translations} translations
 * @returns {SpeechSynthesis | null} synth - The Web Speech API object used for playback.
 *                                   Call `.cancel()` to stop speech.
 */
export const createSpeechPlayer = (rootElement, containerElement, options = ["playPause"], translations) => {
    const textToSpeechContainer = createTextToSpeechContainer(containerElement);

    if (!textToSpeechContainer) {
        console.error(translations.no_container_element_provided);
        return null;
    }

    const text = (rootElement.innerText || "").trim();

    const synth = window.speechSynthesis;
    if (!synth) {
        console.error(translations.web_speech_api_not_supported);
        return null;
    }

    const utterance = new SpeechSynthesisUtterance(text);

    const playerState = {
        isPlaying: false,
        isPaused: false,
    };

    const buttonContainer = prepareButtonContainer(options, synth, utterance, playerState, text, translations);
    const label = document.createElement("label");
    label.className = "wl-speech-player-label";
    label.textContent = translations.listen_to_this_page;

    textToSpeechContainer.appendChild(buttonContainer);
    textToSpeechContainer.appendChild(label);

    return synth;
};
